import {
  Action,
  CombatAuditEntry,
  Combatant,
  CombatState,
} from '@shared/types/Combat';
import { RulesetDefinition } from '@shared/types/Ruleset';
import { resolveRuleset, ResolvedRuleset } from './ruleset';

/**
 * Deterministic, strict rules dispatcher for the shared/backend combat path.
 *
 * The engine accepts only structured action mechanics. It never selects a
 * target, invents a modifier, derives a missing save, or parses descriptions.
 * rule_reference: AGENTS.md deterministic engine, audit trail, strict preconditions.
 * date_of_change: 2026-08-21
 */

export const COMBAT_ENGINE_DOCUMENTATION_PATH = 'Character_Monster_Data_Architecture.md#6-state-machine-and-audit-trail';

export class CombatEngineError extends Error {
  readonly code: string;
  readonly path: string;
  readonly expected: string;
  readonly received: unknown;
  readonly documentation = COMBAT_ENGINE_DOCUMENTATION_PATH;

  constructor(code: string, path: string, expected: string, received: unknown) {
    super(`${path} must be ${expected}. See ${COMBAT_ENGINE_DOCUMENTATION_PATH}.`);
    this.name = 'CombatEngineError';
    this.code = code;
    this.path = path;
    this.expected = expected;
    this.received = received;
  }
}

export interface RulesEngineOptions {
  now?: () => string;
  registeredModuleSchemas?: ReadonlySet<string>;
}

interface RollRecord {
  purpose: string;
  formula: string;
  dice: number[];
  modifier: number;
  total: number;
}

interface ResolutionContext {
  state: CombatState;
  actor: Combatant;
  action: Action;
  rolls: RollRecord[];
  steps: string[];
}

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const idOf = (value: unknown): string => String(value ?? '');
const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const isRecord = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

const requireString = (value: unknown, path: string): string => {
  if (typeof value !== 'string' || !value.trim()) {
    throw new CombatEngineError('INVALID_STRING', path, 'a non-empty string', value);
  }
  return value;
};

const requireNumber = (
  value: unknown,
  path: string,
  options: { min?: number; integer?: boolean } = {},
): number => {
  if (!isFiniteNumber(value)) {
    throw new CombatEngineError('INVALID_NUMBER', path, 'a finite number', value);
  }
  if (options.integer && !Number.isInteger(value)) {
    throw new CombatEngineError('INVALID_INTEGER', path, 'an integer', value);
  }
  if (options.min != null && value < options.min) {
    throw new CombatEngineError('NUMBER_TOO_SMALL', path, `at least ${options.min}`, value);
  }
  return value;
};

const findCombatant = (state: CombatState, combatantId: string, path: string): Combatant => {
  const combatant = state.combatants.find((entry) => (
    [entry.id, entry.characterId, entry.monsterId].some((id) => idOf(id) === combatantId)
  ));
  if (!combatant) {
    throw new CombatEngineError('COMBATANT_NOT_FOUND', path, 'an ID in combatState.combatants', combatantId);
  }
  return combatant;
};

const hashSeed = (seed: string): number => {
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

const randomAt = (seed: string, cursor: number): number => {
  let value = (hashSeed(seed) + Math.imul(cursor + 1, 0x6d2b79f5)) >>> 0;
  value = Math.imul(value ^ (value >>> 15), value | 1);
  value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
  return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
};

// One generator for engine resolution and replay. date_of_change: 2026-09-05
export const rollDie = (state: CombatState, sides: number): number => {
  if (!state.random || typeof state.random.seed !== 'string' || !state.random.seed.trim()) {
    throw new CombatEngineError('RANDOM_STATE_REQUIRED', 'combatState.random.seed', 'a persisted non-empty seed', state.random?.seed);
  }
  requireNumber(state.random.cursor, 'combatState.random.cursor', { min: 0, integer: true });
  const result = Math.floor(randomAt(state.random.seed, state.random.cursor) * sides) + 1;
  state.random.cursor += 1;
  return result;
};

interface DiceFormula {
  count: number;
  sides: number;
  modifier: number;
}

const parseDiceFormula = (formula: unknown, path: string): DiceFormula => {
  if (typeof formula !== 'string') {
    throw new CombatEngineError('INVALID_DICE_FORMULA', path, 'a dice formula such as 1d8+3', formula);
  }
  const match = formula.trim().match(/^(\d+)d(\d+)(?:([+-])(\d+))?$/i);
  if (!match) {
    throw new CombatEngineError('INVALID_DICE_FORMULA', path, 'NdS, NdS+M, or NdS-M', formula);
  }
  const count = Number(match[1]);
  const sides = Number(match[2]);
  const modifierMagnitude = Number(match[4] || 0);
  const modifier = match[3] === '-' ? -modifierMagnitude : modifierMagnitude;
  if (!Number.isInteger(count) || count < 1 || count > 100) {
    throw new CombatEngineError('INVALID_DICE_COUNT', path, 'between 1 and 100 dice', count);
  }
  if (!Number.isInteger(sides) || sides < 2 || sides > 1000) {
    throw new CombatEngineError('INVALID_DIE_SIZE', path, 'a die size between 2 and 1000', sides);
  }
  return { count, sides, modifier };
};

const rollFormula = (
  state: CombatState,
  formula: unknown,
  purpose: string,
  path: string,
  diceMultiplier = 1,
  additionalModifier = 0,
): RollRecord => {
  const parsed = parseDiceFormula(formula, path);
  const dice = Array.from({ length: parsed.count * diceMultiplier }, () => rollDie(state, parsed.sides));
  const modifier = parsed.modifier + additionalModifier;
  return {
    purpose,
    formula: `${parsed.count * diceMultiplier}d${parsed.sides}${modifier >= 0 ? '+' : ''}${modifier}`,
    dice,
    modifier,
    total: Math.max(0, dice.reduce((sum, die) => sum + die, 0) + modifier),
  };
};

const conditionEffects = (ruleset: ResolvedRuleset, combatant: Combatant) => {
  if (!Array.isArray(combatant.conditions)) {
    throw new CombatEngineError('CONDITIONS_REQUIRED', 'combatant.conditions', 'an array of canonical condition IDs', combatant.conditions);
  }
  return combatant.conditions.flatMap((condition, index) => {
    const conditionId = requireString(condition, `combatant.conditions[${index}]`);
    const rule = ruleset.baseRules.conditions[conditionId];
    if (!rule) {
      throw new CombatEngineError(
        'CONDITION_RULE_MISSING',
        `combatant.conditions[${index}]`,
        'an exact condition ID defined by the resolved ruleset',
        conditionId,
      );
    }
    return rule.effects;
  });
};

const requireActorCanAct = (ruleset: ResolvedRuleset, actor: Combatant): void => {
  if (actor.hitPoints.current <= 0) {
    throw new CombatEngineError('ACTOR_AT_ZERO_HP', 'actor.hitPoints.current', 'greater than 0', actor.hitPoints.current);
  }
  if (conditionEffects(ruleset, actor).some((effect) => effect.kind === 'cannot_act')) {
    throw new CombatEngineError('ACTOR_CANNOT_ACT', 'actor.conditions', 'conditions that permit actions', actor.conditions);
  }
};

const resolveRollMode = (
  ruleset: ResolvedRuleset,
  actor: Combatant,
  target: Combatant,
  explicit: Action['rollMode'],
  rangeDisadvantage: boolean,
): 'normal' | 'advantage' | 'disadvantage' => {
  let advantage = explicit === 'advantage';
  let disadvantage = explicit === 'disadvantage' || rangeDisadvantage;
  conditionEffects(ruleset, actor).forEach((effect) => {
    if (effect.kind === 'attack_advantage' && effect.scope === 'attacks_by_creature') advantage = true;
    if (effect.kind === 'attack_disadvantage' && effect.scope === 'attacks_by_creature') disadvantage = true;
  });
  conditionEffects(ruleset, target).forEach((effect) => {
    if (effect.kind === 'attack_advantage' && effect.scope === 'attacks_against_creature') advantage = true;
    if (effect.kind === 'attack_disadvantage' && effect.scope === 'attacks_against_creature') disadvantage = true;
  });
  if (advantage === disadvantage) return 'normal';
  return advantage ? 'advantage' : 'disadvantage';
};

const rollD20 = (
  state: CombatState,
  purpose: string,
  mode: 'normal' | 'advantage' | 'disadvantage',
  modifier: number,
): RollRecord & { natural: number } => {
  const dice = [rollDie(state, 20)];
  if (mode !== 'normal') dice.push(rollDie(state, 20));
  const natural = mode === 'advantage' ? Math.max(...dice)
    : mode === 'disadvantage' ? Math.min(...dice)
      : dice[0];
  return {
    purpose,
    formula: `${mode === 'normal' ? '1d20' : '2d20'}${modifier >= 0 ? '+' : ''}${modifier} (${mode})`,
    dice,
    modifier,
    natural,
    total: natural + modifier,
  };
};

const getTargetContext = (action: Action, targetId: string) => {
  if (!Array.isArray(action.targetContexts)) {
    throw new CombatEngineError('TARGET_CONTEXT_REQUIRED', 'action.targetContexts', 'an array with one entry per target', action.targetContexts);
  }
  const context = action.targetContexts.find((entry) => entry?.targetId === targetId);
  if (!context) {
    throw new CombatEngineError('TARGET_CONTEXT_REQUIRED', `action.targetContexts.${targetId}`, 'distance, line of sight, and cover', context);
  }
  requireNumber(context.distanceFeet, `action.targetContexts.${targetId}.distanceFeet`, { min: 0 });
  if (typeof context.lineOfSight !== 'boolean') {
    throw new CombatEngineError('LINE_OF_SIGHT_REQUIRED', `action.targetContexts.${targetId}.lineOfSight`, 'a boolean', context.lineOfSight);
  }
  if (!['none', 'half', 'three_quarters', 'total'].includes(context.cover)) {
    throw new CombatEngineError('COVER_REQUIRED', `action.targetContexts.${targetId}.cover`, 'none, half, three_quarters, or total', context.cover);
  }
  return context;
};

const validateTargetRange = (action: Action, targetId: string): { rangeDisadvantage: boolean; coverBonus: number } => {
  const context = getTargetContext(action, targetId);
  if (!context.lineOfSight) {
    throw new CombatEngineError('NO_LINE_OF_SIGHT', `action.targetContexts.${targetId}.lineOfSight`, 'true', false);
  }
  if (context.cover === 'total') {
    throw new CombatEngineError('TOTAL_COVER', `action.targetContexts.${targetId}.cover`, 'less than total cover', context.cover);
  }
  if (!action.range || typeof action.range !== 'object') {
    throw new CombatEngineError('RANGE_REQUIRED', 'action.range', 'structured range metadata', action.range);
  }
  if (action.range.units !== 'feet') {
    throw new CombatEngineError('RANGE_UNITS_UNSUPPORTED', 'action.range.units', 'feet', action.range.units);
  }
  const normalRange = action.range.type === 'touch' ? 5 : requireNumber(action.range.value, 'action.range.value', { min: 0 });
  const longRange = action.range.longValue == null
    ? normalRange
    : requireNumber(action.range.longValue, 'action.range.longValue', { min: normalRange });
  if (context.distanceFeet > longRange) {
    throw new CombatEngineError('TARGET_OUT_OF_RANGE', `action.targetContexts.${targetId}.distanceFeet`, `at most ${longRange}`, context.distanceFeet);
  }
  return {
    rangeDisadvantage: context.distanceFeet > normalRange,
    coverBonus: context.cover === 'half' ? 2 : context.cover === 'three_quarters' ? 5 : 0,
  };
};

const damageMultiplier = (target: Combatant, damageType: string): { multiplier: number; reason: string | null } => {
  const response = target.damageResponses;
  if (!response) return { multiplier: 1, reason: null };
  if (response.immunities.includes(damageType)) return { multiplier: 0, reason: `immune to ${damageType}` };
  const resistant = response.resistances.includes(damageType);
  const vulnerable = response.vulnerabilities.includes(damageType);
  if (resistant === vulnerable) return { multiplier: 1, reason: null };
  return resistant
    ? { multiplier: 0.5, reason: `resists ${damageType}` }
    : { multiplier: 2, reason: `vulnerable to ${damageType}` };
};

const applyDamage = (target: Combatant, amount: number): { hpDamage: number; tempHpDamage: number } => {
  const temporaryHitPoints = target.temporaryHitPoints;
  if (temporaryHitPoints != null) requireNumber(temporaryHitPoints, 'target.temporaryHitPoints', { min: 0 });
  const tempHpDamage = Math.min(temporaryHitPoints || 0, amount);
  const hpDamage = Math.min(target.hitPoints.current, Math.max(0, amount - tempHpDamage));
  target.temporaryHitPoints = Math.max(0, (temporaryHitPoints || 0) - tempHpDamage);
  target.hitPoints.current = Math.max(0, target.hitPoints.current - hpDamage);
  return { hpDamage, tempHpDamage };
};

const applyHealing = (target: Combatant, amount: number): number => {
  const restored = Math.min(Math.max(0, target.hitPoints.max - target.hitPoints.current), amount);
  target.hitPoints.current += restored;
  return restored;
};

const requireTargetIds = (action: Action): string[] => {
  if (!Array.isArray(action.targetIds) || action.targetIds.length === 0) {
    throw new CombatEngineError('TARGET_IDS_REQUIRED', 'action.targetIds', 'a non-empty array of explicit combatant IDs', action.targetIds);
  }
  const unique = new Set(action.targetIds);
  if (unique.size !== action.targetIds.length) {
    throw new CombatEngineError('DUPLICATE_TARGET_ID', 'action.targetIds', 'unique target IDs', action.targetIds);
  }
  action.targetIds.forEach((id, index) => requireString(id, `action.targetIds[${index}]`));
  return action.targetIds;
};

const requireDamage = (action: Action): NonNullable<Action['damage']> => {
  if (!Array.isArray(action.damage) || action.damage.length === 0) {
    throw new CombatEngineError('DAMAGE_REQUIRED', 'action.damage', 'a non-empty structured damage array', action.damage);
  }
  action.damage.forEach((entry, index) => {
    parseDiceFormula(entry?.dice, `action.damage[${index}].dice`);
    requireString(entry?.type, `action.damage[${index}].type`);
  });
  return action.damage;
};

export class RulesEngine {
  private readonly ruleset: ResolvedRuleset;
  private readonly now: () => string;

  constructor(ruleset: RulesetDefinition, options: RulesEngineOptions = {}) {
    this.ruleset = resolveRuleset(ruleset, options.registeredModuleSchemas ?? new Set<string>());
    this.now = options.now ?? (() => new Date().toISOString());
  }

  async resolveAction(stateInput: CombatState, actorId: string, actionInput: Action): Promise<CombatState> {
    if (!isRecord(stateInput)) {
      throw new CombatEngineError('COMBAT_STATE_REQUIRED', 'combatState', 'an object', stateInput);
    }
    if (!isRecord(actionInput)) {
      throw new CombatEngineError('ACTION_REQUIRED', 'action', 'a structured action object', actionInput);
    }
    const state = clone(stateInput);
    const action = clone(actionInput) as Action;
    requireString(actorId, 'actorId');
    requireString(action.id, 'action.id');
    requireString(action.name, 'action.name');
    if (!Array.isArray(state.auditTrail)) {
      throw new CombatEngineError('AUDIT_TRAIL_REQUIRED', 'combatState.auditTrail', 'an array', state.auditTrail);
    }
    if (!state.random) {
      throw new CombatEngineError('RANDOM_STATE_REQUIRED', 'combatState.random', '{ seed, cursor }', state.random);
    }
    if (idOf(state.ruleset) !== this.ruleset.id) {
      throw new CombatEngineError('RULESET_MISMATCH', 'combatState.ruleset', this.ruleset.id, state.ruleset);
    }
    if (action.economy !== 'reaction' && idOf(state.activeCombatant) !== actorId) {
      throw new CombatEngineError('OUT_OF_TURN_ACTION', 'combatState.activeCombatant', actorId, state.activeCombatant);
    }

    const actor = findCombatant(state, actorId, 'actorId');
    requireActorCanAct(this.ruleset, actor);
    const context: ResolutionContext = { state, actor, action, rolls: [], steps: [] };

    switch (action.type) {
      case 'attack':
        if (action.resolution !== 'attack_roll') {
          throw new CombatEngineError('RESOLUTION_MISMATCH', 'action.resolution', 'attack_roll', action.resolution);
        }
        this.resolveAttack(context);
        break;
      case 'spell':
      case 'ability':
        this.resolveSpellOrAbility(context);
        break;
      case 'movement':
        if (action.resolution !== 'movement') {
          throw new CombatEngineError('RESOLUTION_MISMATCH', 'action.resolution', 'movement', action.resolution);
        }
        this.resolveMovement(context);
        break;
      default:
        throw new CombatEngineError('ACTION_TYPE_UNSUPPORTED', 'action.type', 'attack, spell, ability, or movement', action.type);
    }

    const auditEntry: CombatAuditEntry = {
      sequence: state.auditTrail.length + 1,
      timestamp: this.now(),
      actorId,
      actionId: action.id as string,
      actionName: action.name,
      rulesetId: this.ruleset.id,
      rulesetVersion: this.ruleset.version,
      appliedOverlayIds: this.ruleset.appliedOverlayIds.slice(),
      steps: context.steps,
      rolls: context.rolls,
    };
    state.auditTrail.push(auditEntry);
    return state;
  }

  private resolveAttack(context: ResolutionContext): void {
    const { action, actor, state, rolls, steps } = context;
    const targetIds = requireTargetIds(action);
    if (targetIds.length !== 1) {
      throw new CombatEngineError('ATTACK_TARGET_COUNT', 'action.targetIds', 'exactly one target for an attack roll', targetIds);
    }
    const target = findCombatant(state, targetIds[0], 'action.targetIds[0]');
    const { rangeDisadvantage, coverBonus } = validateTargetRange(action, targetIds[0]);
    const actionAttackBonus = requireNumber(action.attackBonus, 'action.attackBonus');
    const modifier = actionAttackBonus + this.ruleset.baseRules.attack.attackRollModifier;
    const rollMode = resolveRollMode(this.ruleset, actor, target, action.rollMode, rangeDisadvantage);
    const attackRoll = rollD20(state, `${action.name} attack`, rollMode, modifier);
    rolls.push(attackRoll);

    const attackRules = this.ruleset.baseRules.attack;
    const automaticMiss = attackRules.naturalOneAlwaysMisses && attackRoll.natural === 1;
    const automaticHit = attackRules.naturalTwentyAlwaysHits && attackRoll.natural === 20;
    const targetArmorClass = requireNumber(target.armorClass, 'target.armorClass', { min: 0 }) + coverBonus;
    const hit = !automaticMiss && (automaticHit || attackRoll.total >= targetArmorClass);
    // A widened critical range changes damage only after the attack hits; it
    // must not produce a contradictory "critical miss" result.
    // rule_reference: D&D 5e attack and critical-hit sequence.
    // date_of_change: 2026-08-21
    const critical = hit && attackRoll.natural >= attackRules.criticalHit.threshold;
    steps.push(
      `Attack: ${attackRoll.formula} = ${attackRoll.total} vs AC ${targetArmorClass}; ${hit ? 'hit' : 'miss'}.`,
    );
    if (!hit) return;

    const damage = requireDamage(action);
    damage.forEach((entry, index) => {
      const damageRoll = rollFormula(
        state,
        entry.dice,
        `${action.name} ${entry.type} damage`,
        `action.damage[${index}].dice`,
        critical ? attackRules.criticalHit.damageDiceMultiplier : 1,
        attackRules.damageRollModifier,
      );
      rolls.push(damageRoll);
      const response = damageMultiplier(target, entry.type);
      const adjustedDamage = Math.floor(damageRoll.total * response.multiplier);
      const applied = applyDamage(target, adjustedDamage);
      steps.push(
        `Damage: ${damageRoll.formula} = ${damageRoll.total}${response.reason ? `; target ${response.reason}` : ''} -> ${adjustedDamage}; ${applied.tempHpDamage} temporary HP and ${applied.hpDamage} HP lost.`,
      );
    });
    this.applyConditions(action, target, steps);
  }

  private resolveSpellOrAbility(context: ResolutionContext): void {
    const { action } = context;
    if (action.resolution === 'attack_roll') {
      this.resolveAttack(context);
      return;
    }
    if (action.resolution === 'saving_throw') {
      this.resolveSavingThrow(context);
      return;
    }
    if (action.resolution === 'automatic') {
      this.resolveAutomatic(context);
      return;
    }
    throw new CombatEngineError(
      'RESOLUTION_MISMATCH',
      'action.resolution',
      'attack_roll, saving_throw, or automatic',
      action.resolution,
    );
  }

  private resolveSavingThrow(context: ResolutionContext): void {
    const { action, state, rolls, steps } = context;
    const targetIds = requireTargetIds(action);
    if (!action.save || typeof action.save !== 'object') {
      throw new CombatEngineError('SAVE_REQUIRED', 'action.save', '{ ability, dc, halfDamageOnSuccess }', action.save);
    }
    const ability = requireString(action.save.ability, 'action.save.ability');
    const dc = requireNumber(action.save.dc, 'action.save.dc', { min: 1, integer: true });
    const damage = action.damage ? requireDamage(action) : [];
    if (damage.length > 0 && typeof action.save.halfDamageOnSuccess !== 'boolean') {
      throw new CombatEngineError('SAVE_DAMAGE_POLICY_REQUIRED', 'action.save.halfDamageOnSuccess', 'a boolean', action.save.halfDamageOnSuccess);
    }
    if (targetIds.length > 1 && damage.length > 0 && !action.damageRollMode) {
      throw new CombatEngineError('DAMAGE_ROLL_MODE_REQUIRED', 'action.damageRollMode', 'once or per_target', action.damageRollMode);
    }
    targetIds.forEach((targetId) => validateTargetRange(action, targetId));

    const sharedDamage = damage.length > 0 && action.damageRollMode === 'once'
      ? damage.map((entry, index) => rollFormula(state, entry.dice, `${action.name} ${entry.type} damage`, `action.damage[${index}].dice`))
      : null;
    if (sharedDamage) rolls.push(...sharedDamage);

    targetIds.forEach((targetId) => {
      const target = findCombatant(state, targetId, `action.targetIds.${targetId}`);
      const modifier = target.savingThrowModifiers?.[action.save!.ability];
      if (!isFiniteNumber(modifier)) {
        throw new CombatEngineError('SAVE_MODIFIER_REQUIRED', `combatant.${targetId}.savingThrowModifiers.${ability}`, 'a finite number', modifier);
      }
      const saveRoll = rollD20(state, `${target.name} ${ability} save`, 'normal', modifier);
      rolls.push(saveRoll);
      const succeeded = saveRoll.total >= dc;
      steps.push(`Save: ${saveRoll.formula} = ${saveRoll.total} vs DC ${dc}; ${succeeded ? 'success' : 'failure'}.`);

      const targetDamage = sharedDamage || damage.map((entry, index) => {
        const roll = rollFormula(state, entry.dice, `${action.name} ${entry.type} damage`, `action.damage[${index}].dice`);
        rolls.push(roll);
        return roll;
      });
      targetDamage.forEach((damageRoll, index) => {
        const entry = damage[index];
        const response = damageMultiplier(target, entry.type);
        const saveMultiplier = succeeded && action.save!.halfDamageOnSuccess ? 0.5 : succeeded ? 0 : 1;
        const adjustedDamage = Math.floor(damageRoll.total * saveMultiplier * response.multiplier);
        const applied = applyDamage(target, adjustedDamage);
        steps.push(
          `Damage: ${damageRoll.formula} = ${damageRoll.total}${response.reason ? `; target ${response.reason}` : ''} -> ${adjustedDamage}; ${applied.tempHpDamage} temporary HP and ${applied.hpDamage} HP lost.`,
        );
      });
      if (!succeeded) this.applyConditions(action, target, steps);
    });
  }

  private resolveAutomatic(context: ResolutionContext): void {
    const { action, state, rolls, steps } = context;
    const targetIds = requireTargetIds(action);
    const damage = action.damage ? requireDamage(action) : [];
    const healing = Array.isArray(action.healing) ? action.healing : [];
    if (damage.length === 0 && healing.length === 0 && !(action.conditionsApplied?.length)) {
      throw new CombatEngineError('AUTOMATIC_EFFECT_REQUIRED', 'action', 'damage, healing, or structured conditionsApplied', action);
    }
    healing.forEach((entry, index) => parseDiceFormula(entry?.dice, `action.healing[${index}].dice`));
    if (targetIds.length > 1 && (damage.length > 0 || healing.length > 0) && !action.damageRollMode) {
      throw new CombatEngineError('DAMAGE_ROLL_MODE_REQUIRED', 'action.damageRollMode', 'once or per_target', action.damageRollMode);
    }
    targetIds.forEach((targetId) => validateTargetRange(action, targetId));

    const sharedDamage = action.damageRollMode === 'once'
      ? damage.map((entry, index) => rollFormula(state, entry.dice, `${action.name} ${entry.type} damage`, `action.damage[${index}].dice`))
      : null;
    const sharedHealing = action.damageRollMode === 'once'
      ? healing.map((entry, index) => rollFormula(state, entry.dice, `${action.name} healing`, `action.healing[${index}].dice`))
      : null;
    if (sharedDamage) rolls.push(...sharedDamage);
    if (sharedHealing) rolls.push(...sharedHealing);

    targetIds.forEach((targetId) => {
      const target = findCombatant(state, targetId, `action.targetIds.${targetId}`);
      const targetDamage = sharedDamage || damage.map((entry, index) => {
        const roll = rollFormula(state, entry.dice, `${action.name} ${entry.type} damage`, `action.damage[${index}].dice`);
        rolls.push(roll);
        return roll;
      });
      targetDamage.forEach((damageRoll, index) => {
        const response = damageMultiplier(target, damage[index].type);
        const adjustedDamage = Math.floor(damageRoll.total * response.multiplier);
        const applied = applyDamage(target, adjustedDamage);
        steps.push(`Automatic damage: ${adjustedDamage}; ${applied.tempHpDamage} temporary HP and ${applied.hpDamage} HP lost.`);
      });
      const targetHealing = sharedHealing || healing.map((entry, index) => {
        const roll = rollFormula(state, entry.dice, `${action.name} healing`, `action.healing[${index}].dice`);
        rolls.push(roll);
        return roll;
      });
      targetHealing.forEach((healingRoll) => {
        const restored = applyHealing(target, healingRoll.total);
        steps.push(`Healing: ${healingRoll.formula} = ${healingRoll.total}; ${restored} HP restored.`);
      });
      this.applyConditions(action, target, steps);
    });
  }

  private resolveMovement(context: ResolutionContext): void {
    const { action, actor, state, steps } = context;
    const movement = action.movement;
    if (!movement || typeof movement !== 'object') {
      throw new CombatEngineError('MOVEMENT_REQUIRED', 'action.movement', 'structured movement data', movement);
    }
    const distanceFeet = requireNumber(movement.distanceFeet, 'action.movement.distanceFeet', { min: 0 });
    if (!['normal', 'difficult'].includes(movement.terrain)) {
      throw new CombatEngineError('TERRAIN_REQUIRED', 'action.movement.terrain', 'normal or difficult', movement.terrain);
    }
    if (!isFiniteNumber(actor.movementSpent) || actor.movementSpent < 0) {
      throw new CombatEngineError('MOVEMENT_SPENT_REQUIRED', 'actor.movementSpent', 'a non-negative number', actor.movementSpent);
    }
    const conditionPolicy = conditionEffects(this.ruleset, actor);
    if (conditionPolicy.some((effect) => effect.kind === 'cannot_move')) {
      throw new CombatEngineError('ACTOR_CANNOT_MOVE', 'actor.conditions', 'conditions that permit movement', actor.conditions);
    }
    const conditionSpeedMultiplier = conditionPolicy.reduce(
      (product, effect) => effect.kind === 'speed_multiplier' ? product * effect.value : product,
      1,
    );
    const baseSpeed = movement.mode === 'walk'
      ? actor.speed
      : actor.movementSpeeds?.[movement.mode];
    if (!isFiniteNumber(baseSpeed) || baseSpeed < 0) {
      throw new CombatEngineError('MOVEMENT_SPEED_REQUIRED', `actor.movementSpeeds.${movement.mode}`, 'a non-negative speed', baseSpeed);
    }
    const speed = baseSpeed * conditionSpeedMultiplier;
    const terrainMultiplier = movement.terrain === 'difficult'
      ? this.ruleset.baseRules.movement.difficultTerrainMultiplier
      : 1;
    const cost = distanceFeet * terrainMultiplier;
    if (actor.movementSpent + cost > speed) {
      throw new CombatEngineError('MOVEMENT_EXCEEDS_SPEED', 'action.movement.distanceFeet', `at most ${(speed - actor.movementSpent) / terrainMultiplier}`, distanceFeet);
    }

    const gridSize = requireNumber(state.map.gridSize, 'combatState.map.gridSize', { min: 1 });
    const feetPerCell = requireNumber(state.map.feetPerCell, 'combatState.map.feetPerCell', { min: 0.1 });
    if (state.map.gridType !== 'square') {
      throw new CombatEngineError('GRID_TYPE_UNSUPPORTED', 'combatState.map.gridType', 'square', state.map.gridType);
    }
    const token = state.map.tokens.find((entry) => (
      idOf(entry?.combatantId) === idOf(actor.id) || idOf(entry?.id) === idOf(actor.id)
    ));
    if (!token) {
      throw new CombatEngineError('ACTOR_TOKEN_REQUIRED', 'combatState.map.tokens', `a token for ${idOf(actor.id)}`, state.map.tokens);
    }
    if (token.x !== movement.from.x || token.y !== movement.from.y) {
      throw new CombatEngineError('MOVEMENT_ORIGIN_MISMATCH', 'action.movement.from', `the token origin (${token.x}, ${token.y})`, movement.from);
    }
    const horizontalCells = Math.abs(movement.to.x - movement.from.x) / gridSize;
    const verticalCells = Math.abs(movement.to.y - movement.from.y) / gridSize;
    const diagonalCells = Math.min(horizontalCells, verticalCells);
    const straightCells = Math.max(horizontalCells, verticalCells) - diagonalCells;
    const measuredCells = this.ruleset.baseRules.movement.diagonalMethod === 'five_ten_five'
      ? straightCells + diagonalCells * 1.5
      : Math.sqrt((horizontalCells ** 2) + (verticalCells ** 2));
    const measuredFeet = measuredCells * feetPerCell;
    if (Math.abs(measuredFeet - distanceFeet) > 0.01) {
      throw new CombatEngineError('MOVEMENT_DISTANCE_MISMATCH', 'action.movement.distanceFeet', `${measuredFeet} feet from the supplied coordinates`, distanceFeet);
    }
    token.x = movement.to.x;
    token.y = movement.to.y;
    actor.movementSpent += cost;
    steps.push(`Movement: ${distanceFeet} feet by ${movement.mode}; ${cost} feet of movement spent (${actor.movementSpent}/${speed}).`);
  }

  private applyConditions(action: Action, target: Combatant, steps: string[]): void {
    if (!action.conditionsApplied) return;
    if (!Array.isArray(action.conditionsApplied)) {
      throw new CombatEngineError('CONDITIONS_INVALID', 'action.conditionsApplied', 'an array', action.conditionsApplied);
    }
    action.conditionsApplied.forEach((condition, index) => {
      requireString(condition, `action.conditionsApplied[${index}]`);
      if (!this.ruleset.baseRules.conditions[condition]) {
        throw new CombatEngineError('CONDITION_RULE_MISSING', `action.conditionsApplied[${index}]`, 'a condition defined by the resolved ruleset', condition);
      }
      if (!target.conditions.includes(condition)) target.conditions.push(condition);
      steps.push(`Condition: ${condition} applied to ${target.name}.`);
    });
  }
}
